import { Box, Checkbox as MuiCheckbox, CircularProgress, FormControlLabel } from "@mui/material";
import { usePDF } from "@react-pdf/renderer";
import { AxiosError, AxiosResponse } from "axios";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import Card from "src/components/common/Card";
import DashboardWrapper from "src/components/common/DashboardWrapper";
import MapComponent from "src/components/common/KML";
import ViewForm from "src/components/common/ViewForm";
import ViewImages from "src/components/common/ViewImage";
import { CustomStyles, getStyles } from "src/styles/theme";
import {
  getLandDetailsById,
  getProjectById,
  putDeselectDetails,
  updateAcknowledgeByAdmin,
  updateAcknowledgeAdminRemark,
  updateRejectByAdmin,
} from "src/utils/api/dashboard";
import defaultStyles, { StylesClasses } from "./styles";
import {
  dateAndTimeStamp,
  downloadS3File,
  downloadSheet,
  formatToIndianCurrency,
  getFormattedDate,
} from "src/utils/helper";
import { Button } from "src/components/common/FormComponents/Buttons";
import { useAppSelector } from "src/redux/store";
import { useReactToPrint } from "react-to-print";
import { FileUpload, Input } from "src/components/common/FormComponents";
import { useForm } from "react-hook-form";
import { SuccessModal } from "src/components/common/modal";
import { path } from "src/constants/path";
import { ProjectType } from "src/constants/common";

type WaterProjectProps = {
  projectId: string;
  customStyles?: CustomStyles<StylesClasses>;
};

const SelectedLandDetails = ({}: WaterProjectProps) => {
  const styles = getStyles<StylesClasses>(defaultStyles);
  const [projectData, setProjectData] = useState<any>([]);
  const [pdfLink, setPdfLink] = useState<string>();

  const componentRef = useRef();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const userId = searchParams.get("id");

  const userDetails = useAppSelector((state: any) => state.root.user);
  const role = userDetails?.data?.role;

  const {
    data: userData,
    isLoading,
    refetch,
  } = useQuery(["userDetails", userId], () => getLandDetailsById(userId), {
    onSuccess: (res: AxiosResponse) => {
      setProjectData(res?.data);
    },
    onError: (req: AxiosError) => {},
    enabled: !!userId,
    refetchOnMount: true,
  });

  const projectId = projectData?.projectDetailId;

  const { data: projectDetails, isLoading: isProjectDetailLoading } = useQuery(
    ["projects", projectId],
    () => getProjectById(projectId),
    {
      onSuccess: (res: AxiosResponse) => {},
      onError: (err: AxiosError) => {},
      refetchOnMount: true,
      enabled: !!projectId,
    },
  );

  const [instance] = usePDF({});

  useEffect(() => {
    const blob = instance.blob;

    if (!!blob) {
      const dataURL = URL.createObjectURL(blob);
      setPdfLink(dataURL);
    }
  }, [instance]);

  const exportQuery = (data) => {
    const extractedData = data.map((item) => ({
      Name: item.user.firstName,
      Query: item.query,
      created_at: dateAndTimeStamp(item.created_at, "IST"),
    }));
    downloadSheet(extractedData);
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: "@media print { body { -webkit-print-color-adjust: exact; } }",
  });

  const totalArea = Number(projectData?.totalArea);

  const [deselectState, setDeselectState] = useState(false);
  const [acknowledgeState, setAcknowledgeState] = useState(false);
  const [confirmDeselectOpen, setConfirmDeselectOpen] = useState(false);
  const [confirmAcknowledgeOpen, setConfirmAcknowledgeOpen] = useState(false);

  const {
    control,
    getValues,
    setValue,
    setError,
    clearErrors,
    trigger,
    formState: { errors, isValid },
  } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      adminSelectionRemark: "",
      file: undefined,
      attachments: [],
      deselectAttachments: [],
      adminRejectRemark: "",
    },
  });

  const treeCanopyDensityValue = Number(
    projectData?.landSpecification?.treeCanopyDensity,
  );

  const landArea = Number(projectDetails?.data?.landSpecification?.totalArea);
  const occupiedArea = Number(projectData?.totalArea);

  //video
  const droneVideoFromKmlInfo = projectDetails?.data?.documentDetail?.filter(
    (doc) => doc.type === "DRONE_VIDEO",
  );

  const droneVideoUrl = droneVideoFromKmlInfo?.map((doc) => doc.documentUrl);
  //kml
  const kmlUrlData = projectDetails?.data?.documentDetail?.find(
    (doc) => doc.type === "LAND_KML_FILE",
  );
  const kmlUrl = kmlUrlData?.documentUrl;

  //handle DeSelect start

  const { mutate: mutateReject } = useMutation(
    "handleReject",
    ({ data }: any) => updateRejectByAdmin(userId, data),
    {
      onSuccess: () => {
        setDeselectState(true);
        refetch();
      },
    },
  );

  const handleDeselect = async () => {
    const fileIds = (getValues("deselectAttachments") || []).map(
      (file) => file.key,
    );

    try {
      mutateReject({
        data: {
          adminRejectRemark: getValues("adminRejectRemark"),
          adminSelectionRemark: getValues("adminSelectionRemark"),
          status: "DESELECT",
          fileIds,
        },
      });
    } catch (err) {
      console.error("Failed to deselect:", err);
    }
  };

  //handle Acknowledge start

  const { mutate: mutateAcknowledge } = useMutation(
    "handleAcknowledge",
    ({ data }: any) => updateAcknowledgeAdminRemark(userId, data),
    {
      onSuccess: () => {
        setAcknowledgeState(true);
        refetch();
      },
    },
  );

  const handleAcknowledge = async () => {
    const fileIds = (getValues("attachments") || []).map((file) => file.key);

    try {
      mutateAcknowledge({
        data: {
          adminSelectionRemark: getValues("adminSelectionRemark"),
          status: "ACKNOWLEDGE_BY_APPROVER",
          fileIds,
        },
      });
    } catch (err) {
      console.error("Failed to acknowledge:", err);
    }
  };

  // Gate confirmations behind per-action validation
  const openConfirmAcknowledge = async () => {
    const ok = await trigger(["adminSelectionRemark", "attachments"]);
    if (ok) setConfirmAcknowledgeOpen(true);
  };
  const openConfirmDeselect = async () => {
    const ok = await trigger(["adminRejectRemark", "deselectAttachments"]);
    if (ok) setConfirmDeselectOpen(true);
  };

  //site visited by entity
  const trimTrailingZeros = (value?: string | number) => {
    if (value === null || value === undefined) return "-";

    const num = Number(value);
    if (Number.isNaN(num)) return value;

    return num.toString();
  };

  // Helper function to parse and validate siteVisitedByEntity
  const parseSiteVisitedByEntity = () => {
    try {
      const data = projectData?.siteVisitedByEntity;

      // Check if data exists
      if (!data) return [];

      // If it's a string, try to parse it
      if (typeof data === "string") {
        const parsed = JSON.parse(data);
        return Array.isArray(parsed) ? parsed : [];
      }

      // If it's already an array, return it
      if (Array.isArray(data)) {
        return data;
      }

      // If it's an object, wrap it in an array
      if (typeof data === "object") {
        return [data];
      }

      return [];
    } catch (error) {
      console.error("Error parsing siteVisitedByEntity:", error);
      return [];
    }
  };

  const siteVisitedByEntityList = parseSiteVisitedByEntity();

  return (
    <Box>
      {!isLoading ? (
        <DashboardWrapper
          title="Eco-Restoration Block Details"
          customStyles={{
            container: { position: "relative" },
            children: { paddingBottom: "80px" },
          }}
          buttonText="Print"
          onButtonClick={handlePrint}
          // backPath={path.home}
          showBackBtn
        >
          <Box ref={componentRef} id="printable-content">
            <Box {...styles("projectName")}>
              Activity Registration No: {projectDetails?.data?.registrationNo}
            </Box>
            <Box {...styles("projectName")}>
              Land Selection No: {projectData?.id}
            </Box>

            <Box {...styles("formWrapper")}>
              <Box {...styles("heading")}>Entity Details</Box>

              <ViewForm
                label="Entity Name"
                value={projectData?.user?.firstName}
              />
              <ViewForm label="Email" value={projectData?.user?.email} />
              <ViewForm
                label="Phone Number"
                value={`${projectData?.user?.phoneNumber}`}
              />
            </Box>

            <Box {...styles("formWrapper")}>
              <Box {...styles("heading")}>District Nodal Officer Details</Box>

              <ViewForm
                label="DNO Name"
                value={projectDetails?.data?.implementingOfficer?.firstName}
              />
              <ViewForm label="Email" value={projectDetails?.data?.implementingOfficer?.email} />
              <ViewForm
                label="Phone Number"
                value={`${projectDetails?.data?.implementingOfficer?.phoneNumber}`}
              />
              <ViewForm
                label="Division"
                value={`${projectDetails?.data?.implementingOfficer?.division}`}
              />
            </Box>
            
            <Box {...styles("formWrapper")}>
              <Box {...styles("heading")}>
                Eco-Restoration Block Location Details
              </Box>
              <ViewForm
                label="State"
                value={projectDetails?.data?.address?.stateName}
                valueStyles={{ fontWeight: "400" }}
              />
              <ViewForm
                label="District"
                value={projectDetails?.data?.address?.districtName}
                valueStyles={{ fontWeight: "400" }}
              />
              <ViewForm
                label="Circle"
                value={projectDetails?.data?.landSpecification?.circleName}
                valueStyles={{ fontWeight: "400" }}
              />
              <ViewForm
                label="Forest Division"
                value={projectDetails?.data?.implementingOfficer?.division}
                valueStyles={{ fontWeight: "400" }}
              />
              <ViewForm
                label="Revenue Block"
                value={projectDetails?.data?.landSpecification?.revenueBlock}
                valueStyles={{ fontWeight: "400" }}
              />
              <ViewForm
                label="Range"
                value={projectDetails?.data?.landSpecification?.range}
                valueStyles={{ fontWeight: "400" }}
              />
              <ViewForm
                label="Compartment Number"
                value={
                  projectDetails?.data?.landSpecification?.compartmentNumber
                }
                valueStyles={{ fontWeight: "400" }}
              />
              <ViewForm
                label="Beat"
                value={projectDetails?.data?.landSpecification?.beat}
                valueStyles={{ fontWeight: "400" }}
              />
              <ViewForm
                label="Survey No"
                value={projectDetails?.data?.address?.khasraNo}
                valueStyles={{ fontWeight: "400" }}
              />
              <ViewForm
                label="TreeCanopy Density"
                value={(+projectDetails?.data?.landSpecification
                  ?.treeCanopyDensity).toFixed(2)}
                valueStyles={{ fontWeight: "400" }}
              />
              <ViewForm
                label="Total Land Area"
                value={`${landArea.toFixed(2)} ${projectData?.areaUnit}`}
                valueStyles={{ fontWeight: "400" }}
              />
              <ViewForm
                label="Selected Area"
                value={`${occupiedArea.toFixed(2)} ${projectData?.areaUnit}`}
                valueStyles={{ fontWeight: "400" }}
              />
              <ViewForm
                label="Slope"
                value={projectDetails?.data?.landSpecification?.slope}
                valueStyles={{ fontWeight: "400" }}
              />
              <ViewForm
                label="Terrain (Slope %)"
                value={projectDetails?.data?.landSpecification?.terrain}
                valueStyles={{ fontWeight: "400" }}
              />
              <ViewForm
                label=""
                value={projectDetails?.data?.landSpecification?.slopeAndTerrain}
                valueStyles={{ fontWeight: "400" }}
                noDot
              />
              <ViewForm
                label="Climatic Zone"
                value={projectDetails?.data?.landSpecification?.climaticZone}
                valueStyles={{ fontWeight: "400" }}
              />
              <ViewForm
                label="Accessibility with Distance"
                value={projectDetails?.data?.landSpecification?.accessibility}
                valueStyles={{ fontWeight: "400" }}
              />
            </Box>

            <Box {...styles("formWrapper")}>
              <Box {...styles("heading")}>Eco-Restoration Block Details</Box>

              <ViewForm
                label="Land Type"
                value={projectDetails?.data?.landType}
                valueStyles={{ fontWeight: "400" }}
              />

              <Box
                {...styles("subHeading")}
                style={{ color: "#559891ff", fontWeight: 600, marginTop: "20px" }}
              >
                Edaphic Details
              </Box>
              <Box {...styles("group")}>
                <ViewForm
                  label=""
                  value={projectDetails?.data?.landSpecification?.edaphicDetails}
                  valueStyles={{ fontWeight: "400" }}
                  noDot
                />
                <ViewForm
                  label="Soil Type"
                  value={projectDetails?.data?.landSpecification?.soilType}
                  valueStyles={{ fontWeight: "400" }}
                />
                <ViewForm
                  label="Soil Depth (cm) & Texture"
                  value={projectDetails?.data?.landSpecification?.soilDepthAndTexture}
                  valueStyles={{ fontWeight: "400" }}
                />
                <ViewForm
                  label="Soil Moisture Regime"
                  value={projectDetails?.data?.landSpecification?.soilMoistureRegime}
                  valueStyles={{ fontWeight: "400" }}
                />
                <ViewForm
                  label="Soil pH and fertility status"
                  value={projectDetails?.data?.landSpecification?.soilpH}
                  valueStyles={{ fontWeight: "400" }}
                />
              </Box>

              <Box
                {...styles("subHeading")}
                style={{ color: "#559891ff", fontWeight: 600, marginTop: "20px" }}
              >
                Hydrological Conditions
              </Box>
              <Box {...styles("group")}>
                <ViewForm
                  label=""
                  value={projectDetails?.data?.landSpecification?.hydrologicalConditions}
                  valueStyles={{ fontWeight: "400" }}
                  noDot
                />
                <ViewForm
                  label="Source Of Water"
                  value={projectDetails?.data?.landSpecification?.sourceOfWater}
                  valueStyles={{ fontWeight: "400" }}
                />
                <ViewForm
                  label="Average Ground water level (meters)"
                  value={projectDetails?.data?.landSpecification?.waterSupplyLines}
                  valueStyles={{ fontWeight: "400" }}
                />
                <ViewForm
                  label="Distance to Nearest Water Source (km)"
                  value={projectDetails?.data?.landSpecification?.distanceToNearestWaterSource}
                  valueStyles={{ fontWeight: "400" }}
                />
                <ViewForm
                  label="Average Rainfall (milli meters)"
                  value={projectDetails?.data?.landSpecification?.averagRainfall}
                  valueStyles={{ fontWeight: "400" }}
                />
              </Box>

              <Box {...styles("group")}>
                <ViewForm
                  label="Forest Type"
                  value={projectDetails?.data?.landSpecification?.forestType}
                  valueStyles={{ fontWeight: "400" }}
                />
                <ViewForm
                  label="Type Of Vegetation"
                  value={projectDetails?.data?.landSpecification?.typeOfVegetation}
                  valueStyles={{ fontWeight: "400" }}
                />
                <Box {...styles("group")}>
                  <ViewForm
                    label="Presence of existing trees inside Land parcel"
                    tableValue={(() => {
                      const raw =
                        projectDetails?.data?.landSpecification?.seedlingDetails;
                      try {
                        const parsed =
                          typeof raw === "string" ? JSON.parse(raw) : raw;
                        if (Array.isArray(parsed) && parsed.length > 0) {
                          return (
                            <table
                              width="500"
                              // border={1}
                              style={{ borderCollapse: "collapse" }}
                              align="center"
                            >
                              <thead>
                                <tr>
                                  <th>Common Name</th>
                                  <th>Botanical Name</th>
                                  <th>Number of Trees</th>
                                </tr>
                              </thead>
                              <tbody>
                                {parsed.map((item) => (
                                  <tr key={item.commonName}>
                                    <td style={{ textAlign: "center" }}>
                                      {item.commonName}
                                    </td>
                                    <td style={{ textAlign: "center" }}>
                                      {item.botanicalName}
                                    </td>
                                    <td style={{ textAlign: "center" }}>
                                      {item.numberOfSeedlings}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          );
                        }
                        return "-";
                      } catch {
                        return "-";
                      }
                    })()}
                  />

                  {projectData?.treeSpecificationDetail?.map((data) => {
                    return (
                      <Box
                        {...styles("group")}
                        key={data?.id ?? data?.speciesName}
                      >
                        {data?.treeType === 0 ? (
                          <>
                            <ViewForm
                              label="Existing Tree Species"
                              value={data?.speciesName}
                            />
                            {!!data?.treeSpecies &&
                              Object?.keys(data?.treeSpecies)?.map((tree) => (
                                <ViewForm
                                  key={tree}
                                  label="Name"
                                  value={data?.treeSpecies?.[tree]}
                                />
                              ))}
                            <ViewForm
                              label="Existing Tree count"
                              value={data?.treeCount}
                            />
                          </>
                        ) : (
                          <>
                            <ViewForm
                              label="Tree Species Proposed"
                              value={data?.speciesName}
                            />
                            {!!data?.treeSpecies && (
                              <>
                                <ViewForm
                                  label="Name"
                                  value={data?.treeSpecies?.name}
                                />
                                <ViewForm
                                  label="Species Name"
                                  value={data?.treeSpecies?.speciesName}
                                />
                                <ViewForm
                                  label="Species Type"
                                  value={data?.treeSpecies?.speciesType}
                                />
                              </>
                            )}
                            <ViewForm
                              label="Number Of Trees Proposed"
                              value={data?.treeCount}
                            />
                          </>
                        )}
                      </Box>
                    );
                  })}
                </Box>
                <ViewForm
                  label="Invasive Species Details"
                  value={projectDetails?.data?.landSpecification?.invasiveSpeciesDetails ? "Yes" : "No"}
                  valueStyles={{ fontWeight: "400" }}
                />
                {projectDetails?.data?.landSpecification?.invasiveSpeciesDetails && (
                  <ViewForm
                    label="Invasive Species Details"
                    value={projectDetails?.data?.landSpecification?.invasiveSpeciesDetails}
                    valueStyles={{ fontWeight: "400" }}
                  />
                )}
                <ViewForm
                  label="Existing Fauna"
                  value={projectDetails?.data?.landSpecification?.existingFauna}
                  valueStyles={{ fontWeight: "400" }}
                />
              </Box>

              <Box
                {...styles("subHeading")}
                style={{ color: "#559891ff", fontWeight: 600, marginTop: "20px" }}
              >
                Socio-Environmental Context
              </Box>
              <Box {...styles("group")}>
                <ViewForm
                  label="Name of the nearest village or habitation"
                  value={projectDetails?.data?.landSpecification?.soilCharacteristics}
                  valueStyles={{ fontWeight: "400" }}
                />
                <ViewForm
                  label="Distance to nearest habitation (km)"
                  value={projectDetails?.data?.landSpecification?.proximityToLocalCommunities}
                  valueStyles={{ fontWeight: "400" }}
                />
                <ViewForm
                  label="Rights And Usage"
                  value={projectDetails?.data?.landSpecification?.rightsAndUsage}
                  valueStyles={{ fontWeight: "400" }}
                />
                <ViewForm
                  label="Community Dependency"
                  value={projectDetails?.data?.landSpecification?.communityDependency}
                  valueStyles={{ fontWeight: "400" }}
                />
                <ViewForm
                  label="Any Existing Restrictions"
                  value={projectDetails?.data?.landSpecification?.anyExistingRestrictions}
                  valueStyles={{ fontWeight: "400" }}
                />
              </Box>

              {projectDetails?.data?.projectType === ProjectType.TREE_PLANTATION && (
                <>
                  <Box {...styles("subHeading")}>Tree Specifications:</Box>
                  <ViewForm
                    label="Start Date of Plantation (DD/MM/YYYY)"
                    value={getFormattedDate(projectData?.projectStartDate)}
                    valueStyles={{ fontWeight: "400" }}
                  />
                  <ViewForm
                    label="Activity Duration (in years)"
                    value={projectData?.projectDuration}
                    valueStyles={{ fontWeight: "400" }}
                  />
                </>
              )}
            </Box>

          {projectDetails?.data?.landSpecification?.description && (
            <Box {...styles("formWrapper")}>
              <Box {...styles("heading")}>Additional Information</Box>
              <ViewForm
                label="Additional Information"
                value={projectData?.landSpecification?.description}
                valueStyles={{ fontWeight: "400" }}
              />
            </Box>
          )}

          <Box {...styles("formWrapper")}>
              <Box>
                <ViewImages
                  fileUrl={kmlUrl}
                  label={"KML File"}
                  onDownload={
                    kmlUrl
                      ? () => {
                          const regNo = projectDetails?.data?.registrationNo;
                          const filename = regNo
                            ? `kml_${regNo}.kml`
                            : "kml_file.kml";
                          downloadS3File(kmlUrl, filename);
                        }
                      : undefined
                  }
                />
              </Box>
              <Box
                sx={{
                  border: "2px solid",
                  borderColor: "#65a765",
                  backgroundColor: "#e6ffe6",
                  margin: "30px 0px",
                }}
              >
                <MapComponent kmlUrl={kmlUrl} />
              </Box>
          </Box>
          
          <Box {...styles("formWrapper")}>
            <Box {...styles("imageWrapper")}>
                {projectDetails?.data?.images?.map((item, idx) => {
                  const imageUrl = item?.imageUrl;
                  if (!imageUrl) return null;
                  return (
                    <Card
                      key={imageUrl || idx}
                      title={item?.documentName ?? `GeoTag Image`}
                      subheader={dateAndTimeStamp(item?.updated_at, "IST")}
                      image={imageUrl}
                    />
                  );
                })}
            </Box>
          </Box>

            {/*video*/}
            <Box {...styles("formWrapper")}>
              <Box {...styles("videoFormWrapper")}>
                <Box {...styles("heading")}>Drone Video</Box>
                {!droneVideoUrl || droneVideoUrl.length === 0 ? (
                  "No Drone Video Available"
                ) : (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {droneVideoFromKmlInfo.map((doc, idx) => (
                      <Box key={doc.documentUrl || idx}>
                        <video
                          src={doc.documentUrl}
                          controls
                          width="300px"
                          height="300px"
                          style={{ borderRadius: "8px" }}
                        />
                        <Box
                          title={doc.documentName}
                          sx={{
                            maxWidth: "300px",
                            mt: 1,
                            textAlign: "center",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {doc.documentName ?? `Video ${idx + 1}`}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

        <Box {...styles("formWrapper")}> 
          <Box {...styles("heading")}>While Selection Details</Box>
          {projectData?.utilization && (
            <Box {...styles("videoFormWrapper")}>
              <Box {...styles("heading")}>Utilization</Box>
              {!!projectData?.utilization?.primary && (
                <ViewForm
                  label="Primary"
                  value={projectData?.utilization?.primary?.label}
                  valueStyles={{ fontWeight: "400" }}
                />
              )}
              {!!projectData?.utilization?.secondary && (
                <ViewForm
                  label="Secondary"
                  value={projectData?.utilization?.secondary?.label}
                  valueStyles={{ fontWeight: "400" }}
                />
              )}
            </Box>
          )}
          {projectData?.nonTradableCredit && (
            <>
              <Box {...styles("videoFormWrapper")}>
                <Box {...styles("heading")}>Entity Declaration</Box>
                <Box>
                  The Green Credits issued for tree plantation under GCP shall
                  be non-tradable.
                </Box>
                <Box mt={2}>
                  The Green credits issued for the tree plantation under the GCP
                  shall be non-transferable except between the holding company
                  and its subsidiary companies as mentioned by the GCA on the
                  GCP portal at the time of registration of the GCA.
                </Box>
              </Box>
            </>
          )}
          {projectData?.siteVisitConfirmation && (
            <Box {...styles("videoFormWrapper")}>
              <Box {...styles("heading")}>Entity Undertaking</Box>
              <FormControlLabel
                control={<MuiCheckbox checked={true} disabled />}
                label="We hereby confirm our participation for restoration of the above-mentioned land parcel."
              />
            </Box>
          )}
        </Box>
          {projectData?.siteDeselect && (
            <Box {...styles("formWrapper")}>
              <Box {...styles("heading")}>Entity Undertaking</Box>
              <FormControlLabel
                control={<MuiCheckbox checked={true} disabled />}
                label="We hereby voluntarily request deselection of the selected site/block and acknowledge that the same may be made available for selection by other eligible entities through the GCP Portal as per applicable guidelines."
              />
            </Box>
          )}

        <Box {...styles("formWrapper")}> 
          <Box {...styles("heading")}>Site Visit Details</Box>

          {(projectData?.siteVisitConfirmation || projectData?.siteDeselect) &&
            siteVisitedByEntityList.length > 0 && (
              <>
                <Box {...styles("videoFormWrapper")}>
                  <Box {...styles("heading")}>Name of Representative</Box>
                  <table
                    width="100%"
                    style={{ borderCollapse: "collapse", marginTop: "10px" }}
                  >
                    <thead>
                      <tr style={{ borderBottom: "2px solid #ddd" }}>
                        <th
                          style={{
                            padding: "8px",
                            textAlign: "left",
                            fontWeight: "600",
                          }}
                        >
                          Name
                        </th>
                        <th
                          style={{
                            padding: "8px",
                            textAlign: "left",
                            fontWeight: "600",
                          }}
                        >
                          Designation
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {siteVisitedByEntityList.map((representative, index) => (
                        <tr
                          key={index}
                          style={{ borderBottom: "1px solid #eee" }}
                        >
                          <td style={{ padding: "8px", textAlign: "left" }}>
                            {representative?.name?.trim() || "-"}
                          </td>
                          <td style={{ padding: "8px", textAlign: "left" }}>
                            {representative?.designation?.trim() || "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              </>
            )}

          {projectData?.siteVisitConfirmation && (
            <>
              <Box {...styles("videoFormWrapper")}>
                <Box {...styles("heading")}>Site Selection Remark</Box>
                <ViewForm
                  label="Remarks"
                  value={projectData?.siteVisitConfirmationRemarks}
                  valueStyles={{ fontWeight: "400" }}
                />
                <ViewForm
                  label="Date"
                  value={projectData?.siteVisitFirstDate}
                  valueStyles={{ fontWeight: "400" }}
                />
              </Box>
            </>
          )}

          {projectData?.siteDeselect && (
            <Box {...styles("videoFormWrapper")}>
              <Box {...styles("heading")}>Site Deselect Remark</Box>
              <ViewForm
                label="Remarks"
                value={projectData?.siteDeselectReason}
                valueStyles={{ fontWeight: "400" }}
              />
              <ViewForm
                label="Date"
                value={projectData?.siteVisitFirstDate}
                valueStyles={{ fontWeight: "400" }}
              />
            </Box>
          )}

          {(projectData?.status === "SITE_VISITED" ||
          projectData?.status === "ACKNOWLEDGE_BY_APPROVER" ||
          projectData?.status === "DESELECT"
            ) && (
            <Box {...styles("videoFormWrapper")}>
              <Box {...styles("heading")}>Site Visit Documents</Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
                  gap: 2,
                  mt: 2,
                }}
              >
                {projectData?.siteVisitDocs?.map((doc, idx) => (
                  <Card
                    key={doc?.fileId || idx}
                    title={`Site Visit Document ${idx + 1}`}
                    subheader={dateAndTimeStamp(doc?.uploadedAt, "IST")}
                    image={doc?.documentUrl}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>

          {projectData?.status === "ACKNOWLEDGE_BY_APPROVER" && (
            <>
              <Box {...styles("formWrapper")}>
                <Box {...styles("heading")}>Admin Remark</Box>
                <ViewForm
                  label="Admin Remark"
                  value={projectData?.adminSelectionRemark}
                  valueStyles={{ fontWeight: "400" }}
                />
              </Box>
              {/* Admin Approval Documents */}
              {(() => {
                const adminDocs = projectData?.adminApproveDocs || [];
                return adminDocs && adminDocs.length > 0 ? (
                  <Box {...styles("formWrapper")}>
                    <Box {...styles("heading")}>Admin Approval Documents</Box>
                    <Box {...styles("documentGroup")}>
                      {adminDocs.map((doc, idx) => (
                        <ViewImages
                          key={doc?.documentUrl || idx}
                          fileUrl={doc?.documentUrl}
                          label={""}
                        />
                      ))}
                    </Box>
                  </Box>
                ) : null;
              })()}
            </>
          )}

          {projectData?.status === "DESELECT" && (
            <>
              <Box {...styles("formWrapper")}>
                <Box {...styles("heading")}>Admin Reject Remark</Box>
                {projectData?.adminRejectRemark && (
                  <ViewForm
                    label="Admin Deselect Remark"
                    value={projectData?.adminRejectRemark}
                    valueStyles={{ fontWeight: "400" }}
                  />
                )}
                {projectData?.adminSelectionRemark && (
                  <ViewForm
                    label="Admin Deselect Remark"
                    value={projectData?.adminSelectionRemark}
                    valueStyles={{ fontWeight: "400" }}
                  />
                )}
              </Box>
              {/* Admin Reject Documents */}
              {(() => {
                const adminDocs = projectData?.adminRejectDocs || [];
                return adminDocs && adminDocs.length > 0 ? (
                  <Box {...styles("formWrapper")}>
                    <Box {...styles("heading")}>Admin Reject Document</Box>
                    <Box {...styles("documentGroup")}>
                      {adminDocs.map((doc, idx) => (
                        <ViewImages
                          key={doc?.documentUrl || idx}
                          fileUrl={doc?.documentUrl}
                          label={""}
                        />
                      ))}
                    </Box>
                  </Box>
                ) : null;
              })()}
            </>
          )}
          {/* Deselect remark and image */}
          {/* {projectData?.comments[0]?.message && (
            <Box {...styles("formWrapper")}>
              <Box {...styles("heading")}>Deselect Details</Box>
              <ViewForm
                label="Remarks"
                value={projectData?.comments[0]?.message}
              />
              {!!projectData?.deselectedFiles?.length && (
                <Box
                  {...styles("documentGroup", {
                    mt: "20px",
                    flexDirection: "column",
                  })}
                >
                  {projectData?.deselectedFiles.map((item) => (
                    <ViewImages imageId={item.fileId} />
                  ))}
                </Box>
              )}
            </Box>
          )} */}
          {(projectData?.status === "SITE_VISITED" || projectData?.status === "PROVISIONAL_SELECTED") && role === "Approver" && (
            <Box {...styles("formWrapper")}>
              <Box sx={{ marginTop: "30px" }}>
                {projectData?.siteVisitConfirmation && (
                  <Input
                    name="adminSelectionRemark"
                    label="Admin Remark for Acknowledgement"
                    control={control}
                    errors={errors}
                    multiline
                    height="360px"
                    customStyles={{
                      input: {
                        height: "auto",
                        fontFamily: "Poppins",
                        width: { sm: "100%", lg: "60%" },
                      },
                    }}
                    rules={{
                      validate: {
                        isLength: (value) => {
                          return value && value["length"] >= 1;
                        },
                      },
                    }}
                  />
                )}

                {projectData?.siteVisitConfirmation && (
                  <FileUpload
                    name="attachments"
                    label="Upload signed acknowledgement letter"
                    control={control}
                    maxLimit={1}
                    supportedFormatMessage="Accepted: images (jpg, png, webp) and PDF."
                    setValue={setValue}
                    getValues={getValues}
                    setError={setError}
                    clearErrors={clearErrors}
                    trigger={trigger}
                    required={true}
                    rules={{
                      validate: {
                        isLength: (value) => {
                          return value && value["length"] >= 1;
                        },
                      },
                    }}
                  />
                )}

                {(projectData?.siteDeselect || projectData?.status === "PROVISIONAL_SELECTED") && (
                  <Input
                    name="adminRejectRemark"
                    label="Admin Remark for Rejection"
                    control={control}
                    errors={errors}
                    multiline
                    height="360px"
                    customStyles={{
                      input: {
                        height: "auto",
                        fontFamily: "Poppins",
                        width: { sm: "100%", lg: "60%" },
                      },
                    }}
                    rules={{
                      validate: {
                        isLength: (value) => {
                          return value && value["length"] >= 1;
                        },
                      },
                    }}
                  />
                )}

                {(projectData?.siteDeselect || projectData?.status === "PROVISIONAL_SELECTED") && (
                  <FileUpload
                    name="deselectAttachments"
                    label="Upload photo / document for Deselect"
                    control={control}
                    maxLimit={1}
                    supportedFormatMessage="Accepted: images (jpg, png, webp) and PDF."
                    setValue={setValue}
                    getValues={getValues}
                    setError={setError}
                    clearErrors={clearErrors}
                    trigger={trigger}
                    required={false}
                  />
                )}

                {/* 🔽 This is the only change you need */}
                <Box
                  display="flex"
                  justifyContent="space-between"
                  width="100%"
                  mt={2}
                >
                  <Button
                    type="submit"
                    text="Deselect"
                    onClick={openConfirmDeselect}
                  />
                  {projectData?.siteVisitConfirmation && (
                    <Button
                      type="submit"
                      text="Acknowledge"
                      onClick={openConfirmAcknowledge}
                    />
                  )}
                  {/* Confirm Deselect Modal */}
                  <SuccessModal
                    open={confirmDeselectOpen}
                    heading="Do you want to deselect this land parcel?"
                    subheading="This action will mark the parcel as deselected."
                    closeOnBackDrop={false}
                    handleModalClose={() => setConfirmDeselectOpen(false)}
                  >
                    <Box display="flex" justifyContent="center" gap={2} mt={2}>
                      <Button
                        text="Cancel"
                        onClick={() => setConfirmDeselectOpen(false)}
                      />
                      <Button
                        text="OK"
                        onClick={async () => {
                          await handleDeselect();
                          setConfirmDeselectOpen(false);
                        }}
                      />
                    </Box>
                  </SuccessModal>

                  {/* Confirm Acknowledge Modal */}
                  <SuccessModal
                    open={confirmAcknowledgeOpen}
                    heading="Do you want to acknowledge this land parcel?"
                    subheading="This action will mark the parcel as acknowledged."
                    closeOnBackDrop={false}
                    handleModalClose={() => setConfirmAcknowledgeOpen(false)}
                  >
                    <Box display="flex" justifyContent="center" gap={2} mt={2}>
                      <Button
                        text="Cancel"
                        onClick={() => setConfirmAcknowledgeOpen(false)}
                      />
                      <Button
                        text="OK"
                        onClick={async () => {
                          await handleAcknowledge();
                          setConfirmAcknowledgeOpen(false);
                        }}
                      />
                    </Box>
                  </SuccessModal>
                </Box>
              </Box>
            </Box>
          )}
          <SuccessModal
            open={deselectState}
            heading="Land Deselected Successfully"
            handleModalClose={() => {
              setDeselectState(false);
              navigate(path.selected);
            }}
          />

          <SuccessModal
            open={acknowledgeState}
            heading="Acknowledge Successfully"
            handleModalClose={() => {
              setAcknowledgeState(false);
              navigate(path.selected);
            }}
          />
        </DashboardWrapper>
      ) : (
        <Box {...styles("loading")}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};

export default SelectedLandDetails;
